import React from 'react';
import styled from 'styled-components';

import { RadiusOption } from 'src/gql/graphql';
import { useGetCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import { ConditionalLink } from 'src/utils/conditional-link';
import { Category } from '../categories-nav.types';
import { categoryCardFocusRing } from './category-card-focus';

const radiusOptions = {
  [RadiusOption.rounded]: '9999px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

type TextCardProps = {
  category: Category;
  style: RadiusOption;
  isMobileGrid: boolean;
};

export const TextCard = ({ category, style, isMobileGrid }: TextCardProps): JSX.Element => {
  const { key, label, slug, customCategory, isOutlet } = category;

  const href = useGetCustomCategoryLink(customCategory, slug, key);

  return (
    <ConditionalLink url={href}>
      <Anchor
        href={href}
        $radius={style}
        $isMobileGrid={isMobileGrid}
        data-testid='category-list-link'
        onClick={category.onClick}
      >
        <Label $isOutlet={isOutlet}>{label}</Label>
      </Anchor>
    </ConditionalLink>
  );
};

const Anchor = styled.a<{ $radius: RadiusOption; $isMobileGrid?: boolean }>`
  background: var(--dcm-categories-card-bg);
  border: 2px solid var(--dcm-categories-card-border-color);
  border-radius: ${({ $radius }) => radiusOptions[$radius]};
  color: var(--dcm-categories-card-text-color);
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: ${24 / 16};
  padding: ${({ $isMobileGrid }) => ($isMobileGrid ? `12px 5px` : `12px 20px`)};
  text-align: center;
  transition: border 0.2s;
  white-space: nowrap;

  ${categoryCardFocusRing}

  @media screen and (min-width: 600px) {
    padding: 12px 20px;
  }

  &:hover {
    border-color: var(--dcm-categories-card-text-color);
  }
`;

const Label = styled.h3<{ $isOutlet?: boolean }>`
  margin: 0;
  ${({ $isOutlet }) =>
    $isOutlet &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;
